import { TypeOf } from "./types";

type UnknownFn = (...args: unknown[]) => unknown;

type KeysOf<T> = {
    [K in keyof T]-?: T[K] extends UnknownFn ? K : never
}[keyof T];

type HookMiddleware<Fn extends UnknownFn> = (
    this: ThisParameterType<Fn>,
    next: (this: ThisParameterType<Fn>, ...args: Parameters<Fn>) => ReturnType<Fn>,
    ...args: Parameters<Fn>
) => ReturnType<Fn>;

type HookPrototypeFunction = <
    Prototype extends object,
    Key extends KeysOf<Prototype>,
    Fn extends Extract<Prototype[Key], UnknownFn> = Extract<Prototype[Key], UnknownFn>
>(
    constructor: TypeOf<Prototype>,
    key: Key,
    middleware: HookMiddleware<Fn>
) => void;

type HookInstanceFunction = <
    Prototype extends object,
    Key extends KeysOf<Prototype>,
    Fn extends Extract<Prototype[Key], UnknownFn> = Extract<Prototype[Key], UnknownFn>
>(
    instance: Prototype,
    key: Key,
    middleware: HookMiddleware<Fn>
) => void;

/** Helpers */
function hookFunction<
    Prototype extends object,
    Key extends KeysOf<Prototype>,
    Fn extends Extract<Prototype[Key], UnknownFn> = Extract<Prototype[Key], UnknownFn>
>(target: Prototype, key: Key, middleware: HookMiddleware<Fn>): void {
    const original = target[key] as Fn | undefined;

    target[key] = function (
        this: ThisParameterType<Fn>,
        ...args: Parameters<Fn>
    ): ReturnType<Fn> {
        const next = (...nextArgs: Parameters<Fn>): ReturnType<Fn> => {
            return original?.apply(this, nextArgs) as ReturnType<Fn>;
        };

        return middleware.call(this, next, ...args);
    } as Prototype[Key];
}

export const hookPrototypeFunction: HookPrototypeFunction = function (
    constructor,
    key,
    middleware
) {
    hookFunction(constructor.prototype, key, middleware);
};

export const hookInstanceFunction: HookInstanceFunction = function (
    instance,
    key,
    middleware
) {
    hookFunction(instance, key, middleware);
};
